/**
 * water_interact — the PURE rules of the water-interaction wave (sweep 2026-10-05, W1–W7: "add water interactibility
 * when vehicles go over it, a player interacts with the water, etc."). No three.js, no DOM: `verify:water-interact`
 * pins every number. The runtime halves live in the modules that already own each act (WildsModule's shot,
 * AtlasVehicleModule's bolts / ride / flight, PhysicsModule's water mode, FootprintDecalModule's strides,
 * AtlasWaterModule's field + emitters, AtlasWaterAudioModule's loops); the one field and its gates are `water_fx`.
 *
 *   W1 SHOTS      a shot's ray marched against the STILL level (flat per body — sea, lake, river): the first DOWNWARD
 *                 entry, refined by bisection. The impact by projectile kind — a slug PLOPS, energy HISSES, a shell or
 *                 the ship cannon throws a column — is a splash, a ring and a voice, and NOTHING on the bed (no decal,
 *                 no dust, no gouge: the bolt never reaches it).
 *   W2 DOWNWASH   a craft within its BAND over the water (26 m for a 15 m hull, ∝ √size, 12…60 m) presses spray and a
 *                 ring of waves under it: quadratic in the height left in the band, rising with speed / thrust. Rings
 *                 are rate-limited per craft (a hovering ship cannot flood the ripple field). The local ship's hull
 *                 SKIMS the surface (it never flies under it) and each BREACH — the hull meeting the water — splashes
 *                 once (Niagara's breach anchors: fire on the crossing, not every frame).
 *   W3/W4 CRAFT   another player's or an NPC's hovercraft ON the water (≤ 4 m over it) wakes it and throws spray; a
 *                 ship / freighter low over it raises downwash — read from ONE choke point (lib/audio/craft_feed.ts).
 *   W5 VOICES     the spray loop's and the downwash loop's mix, the swim STROKE clock (a crawl is ~1.4 strokes a
 *                 second, not a footfall per 0.7 m).
 *   W6 WETNESS    leaving deep water (a swim, a wade past the hips) DRIPS for a few seconds and leaves WET prints for
 *                 ~20 s — the soak decides how long.
 *   W7 TIERS      rings follow the fluid flag (the ripple field); particles follow the particle budget and NEVER run
 *                 on minimal (nothing on its GPU); sounds play on every tier (audio is not the GPU's cost).
 */
import { splashFor, SPEED_SWIM_MPS, SWIM_DEPTH_M, type Stamp } from './water_physics.js';

function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }
function clamp01(v: number): number { return clamp(v, 0, 1); }

// ── W1: a shot meets the water ─────────────────────────────────────────────────────────────────────────────────────
/** The still level over (x, z), null on dry ground (water_fx.waterStillAt). */
export type StillAt = (x: number, z: number) => number | null;
export interface WaterCrossing { /** Distance along the ray, m. */ t: number; x: number; y: number; z: number }

/** March spacing along a ray: fine where the eye can judge it, coarser far out (a river 600 m off is 10 m samples). */
export function crossStep(t: number): number { return t < 150 ? 1.5 : t < 600 ? 4 : 10; }
/** Bisection steps once a sample lands under the surface (1.5 m / 2⁸ ≈ 6 mm). */
export const CROSS_REFINE = 8;

/**
 * The first point where the ray (x0,y0,z0) + t·(dx,dy,dz) ENTERS the water from above, for t ≤ maxM — or null.
 * The surface is the STILL level (flat per body), so only a DESCENDING ray can enter it, and a ray that starts under
 * it (a diver's muzzle) only ever leaves: both are null without a single sample. The caller passes `maxM` = the solid
 * hit's distance (water lies above its bed, so a crossing beyond the first solid is never the first thing struck).
 */
export function firstWaterCrossing(x0: number, y0: number, z0: number, dx: number, dy: number, dz: number, maxM: number, stillAt: StillAt): WaterCrossing | null {
  const len = Math.hypot(dx, dy, dz);
  if (!(len > 0) || !(maxM > 0) || !Number.isFinite(maxM)) return null;
  const ux = dx / len, uy = dy / len, uz = dz / len;
  if (uy >= 0) return null;
  const under = (t: number): boolean => { const s = stillAt(x0 + ux * t, z0 + uz * t); return s !== null && y0 + uy * t <= s; };
  if (under(0)) return null;
  let t = 0;
  while (t < maxM) {
    const nt = Math.min(maxM, t + crossStep(t));
    if (under(nt)) {
      let lo = t, hi = nt;
      for (let i = 0; i < CROSS_REFINE; i++) { const m = (lo + hi) * 0.5; if (under(m)) hi = m; else lo = m; }
      const x = x0 + ux * hi, z = z0 + uz * hi;
      const s = stillAt(x, z);
      return { t: hi, x, y: s ?? y0 + uy * hi, z };
    }
    t = nt;
  }
  return null;
}

/** What struck the water: the hand weapons' projectile kinds, and the ship's cannon. */
export type WaterShotKind = 'slug' | 'bolt' | 'pellets' | 'shell' | 'ring' | 'lance' | 'cannon';
export type WaterShotKey = 'sfx_water_plop' | 'sfx_water_hiss';
export interface WaterShot { key: WaterShotKey; gain: number; rate: number; count: number; speed: number; stamp: Stamp }

/**
 * The water's answer by projectile (the splash grows with impact energy — speed × mass; a cavity then a column):
 * a slug's thin cavity PLOPS, a scatter of pellets patters (smaller, brighter), an energy bolt / ring / lance boils a
 * pocket of water and HISSES, a shell or the ship's cannon throws a column and a wide ring.
 */
export function waterShotFor(kind: WaterShotKind): WaterShot {
  switch (kind) {
    case 'pellets': return { key: 'sfx_water_plop', gain: 0.42, rate: 1.25, count: 10, speed: 2.6, stamp: { strength: -0.03, radius: 0.3 } };
    case 'shell': return { key: 'sfx_water_plop', gain: 0.8, rate: 0.68, count: 60, speed: 5.5, stamp: { strength: -0.12, radius: 1.2 } };
    case 'bolt': return { key: 'sfx_water_hiss', gain: 0.5, rate: 1, count: 12, speed: 2.4, stamp: { strength: -0.04, radius: 0.35 } };
    case 'ring': return { key: 'sfx_water_hiss', gain: 0.55, rate: 0.85, count: 18, speed: 2.8, stamp: { strength: -0.06, radius: 0.6 } };
    case 'lance': return { key: 'sfx_water_hiss', gain: 0.45, rate: 1.15, count: 8, speed: 2, stamp: { strength: -0.035, radius: 0.3 } };
    case 'cannon': return { key: 'sfx_water_hiss', gain: 0.75, rate: 0.7, count: 70, speed: 5, stamp: { strength: -0.14, radius: 1.6 } };
    default: return { key: 'sfx_water_plop', gain: 0.55, rate: 1, count: 16, speed: 3.2, stamp: { strength: -0.05, radius: 0.35 } }; // slug
  }
}
/** The water-shot voices' distance model (ONE DISTANCE OWNER: the call site's gain × inverseDistanceGain(d, ref, max)). */
export const WATER_SHOT_REF_M = 6;
export const WATER_SHOT_MAX_M = 260;
/** A body in the water is still the bolt's target: a crossing this far before the mark is the ENTRY splash on the way
 *  to a submerged torso (a swimmer's chest is ~0.25 m under), not a miss into the lake. */
export const BODY_ENTRY_M = 0.4;

// ── W2 / W4: downwash ──────────────────────────────────────────────────────────────────────────────────────────────
/** The reference hull (a mid fighter) and its band over the water. */
export const DOWNWASH_REF_HULL_M = 15;
export const DOWNWASH_BAND_M = 26;
/** Spray particles a second, all of one craft's ring together (the shared splash pool is 600). */
export const DOWNWASH_SPRAY_MAX_PER_S = 110;
/** One craft's ring stamp at most this often (the rate limit that keeps a hovering ship from flooding the field). */
export const DOWNWASH_RING_EVERY_S = 0.2;
/** Below this intensity a craft disturbs nothing. */
export const DOWNWASH_MIN_K = 0.02;

/** How high over the water a hull's downwash still reaches it: 26 m for a 15 m hull, ∝ √size, clamped 12…60 m. */
export function downwashBand(hullM: number): number {
  return clamp(DOWNWASH_BAND_M * Math.sqrt(Math.max(1, hullM) / DOWNWASH_REF_HULL_M), 12, 60);
}

export interface Downwash {
  /** Intensity 0..1 (0 = out of the band). */
  k: number;
  sprayPerS: number;
  /** Radius of the spray ring around the point under the hull (the sheet spreads with height). */
  sprayRadius: number;
  /** The ring stamp (a dish of waves under the hull), rate-limited by the caller. */
  ring: Stamp;
  /** The loop's mix before distance (the local ship is head-locked; others multiply by their distance gain). */
  gain: number;
  rate: number;
}
const NO_DOWNWASH: Downwash = Object.freeze({ k: 0, sprayPerS: 0, sprayRadius: 0, ring: Object.freeze({ strength: 0, radius: 0 }), gain: 0, rate: 1 }) as Downwash;

/**
 * A hull `heightM` over the live surface: spray and waves under it. Falls QUADRATICALLY with the height left in the
 * band (the jet spreads and slows — the parallel42 downwash notes: spray scaled by size × power, falling with
 * height), rises with the drive — the larger of the throttle and the airspeed over 60 m/s (a hover at idle still
 * presses 45 %). Size scales the spray and the ring by √(hull / 15 m).
 */
export function downwashFor(heightM: number, hullM: number, speedMps: number, thrust01: number, out?: Downwash): Downwash {
  const band = downwashBand(hullM);
  const fall = heightM < band && heightM > -3 ? (1 - clamp01(heightM / band)) ** 2 : 0;
  const drive = clamp01(Math.max(thrust01, speedMps / 60));
  const k = fall * (0.45 + 0.55 * drive);
  if (!(k >= DOWNWASH_MIN_K)) {
    if (!out) return NO_DOWNWASH;
    out.k = 0; out.sprayPerS = 0; out.sprayRadius = 0; out.ring.strength = 0; out.ring.radius = 0; out.gain = 0; out.rate = 1;
    return out;
  }
  const size = Math.sqrt(clamp(hullM, 4, 80) / DOWNWASH_REF_HULL_M);
  // `out` (a per-frame caller's own record) is filled in place — nothing allocated on the hot path.
  const o = out ?? { k: 0, sprayPerS: 0, sprayRadius: 0, ring: { strength: 0, radius: 0 }, gain: 0, rate: 1 };
  o.k = k;
  o.sprayPerS = Math.min(DOWNWASH_SPRAY_MAX_PER_S, 70 * k * size);
  o.sprayRadius = 0.3 * hullM + 0.25 * Math.max(0, heightM);
  o.ring.strength = -(0.012 + 0.05 * k) * Math.min(2, size);
  o.ring.radius = Math.min(6, 0.8 + 0.12 * hullM + 0.08 * Math.max(0, heightM));
  o.gain = 0.55 * k ** 0.8;
  o.rate = 0.85 + 0.3 * drive;
  return o;
}
/** A fresh record for `downwashFor(…, out)` (one per caller, kept). */
export function newDownwash(): Downwash { return { k: 0, sprayPerS: 0, sprayRadius: 0, ring: { strength: 0, radius: 0 }, gain: 0, rate: 1 }; }

/** Whole particles due this tick from a per-second rate, carrying the fraction (never a burst after a hitch). */
export const SPRAY_EMIT_MAX = 24;
export function sprayThisTick(perS: number, dt: number, carry: { debt: number }): number {
  if (!(perS > 0) || !(dt > 0)) { carry.debt = 0; return 0; }
  carry.debt = Math.min(SPRAY_EMIT_MAX, carry.debt + perS * Math.min(dt, 0.1));
  const n = Math.floor(carry.debt);
  carry.debt -= n;
  return n;
}

/** Per-id cooldowns on a caller's clock (seconds): `due` is true at most once per `everyS` for each id. */
export class RateGate {
  private readonly last = new Map<string, number>();
  due(id: string, nowS: number, everyS: number): boolean {
    const t = this.last.get(id);
    if (t !== undefined && nowS - t < everyS && nowS >= t) return false;
    this.last.set(id, nowS);
    return true;
  }
  /** Forget ids not seen for `ageS` (the map must not grow with every hull that ever flew). */
  prune(nowS: number, ageS: number): void { for (const [id, t] of this.last) if (nowS - t > ageS || nowS < t) this.last.delete(id); }
  get size(): number { return this.last.size; }
}

// ── the local ship skims ───────────────────────────────────────────────────────────────────────────────────────────
/** The ship's centre over the live surface at its lowest: the belly skims, the hull never flies under the water. */
export const SHIP_SKIM_M = 1.6;
/** Horizontal speed shed per second while the belly is in the water (a skim costs speed — never a wall). */
export const SKIM_DRAG_PER_S = 0.55;
/** Water is soft only when met flat: the impact a skim is judged by is the VERTICAL closing speed (a 100 m/s pass
 *  grazing the swell is a skim, a 20 m/s belly-flop is a crash). */
export function skimImpactMps(vyMps: number): number { return Math.max(0, -vyMps); }
/** One breach (the hull meeting the water) = one splash, sized by the vertical speed and the pass, and the hull. */
export function breachSplash(vyDownMps: number, speedMps: number, hullM: number): ReturnType<typeof splashFor> {
  return splashFor(Math.max(vyDownMps, speedMps * 0.12), clamp(hullM * 0.3, 1, 8));
}

// ── W3 / W4: other craft over the water ────────────────────────────────────────────────────────────────────────────
export type CraftWaterKind = 'hover' | 'ship' | 'freighter';
/** Default hull lengths when a report carries none (VersonaTravel's fits; the cargo hauler). */
export const CRAFT_HULL_M: Readonly<Record<CraftWaterKind, number>> = { hover: 8.5, ship: 15, freighter: 40 };
/** A hover craft this close over the surface RIDES it (a wake + spray); higher it is airborne off a crest. */
export const HOVER_ON_WATER_M = 4;
/** Craft farther than this from the viewer disturb nothing drawn (the ripple window is ≤ 128 m; spray reads ~250 m). */
export const CRAFT_WATER_RANGE_M = 260;
/** At most this many other craft are worked per frame (nearest first). */
export const CRAFT_WATER_MAX = 6;

/** What a craft does to the water under it: a hover craft on it WAKES it, a ship / freighter low over it DOWNWASHES. */
export function craftWaterMode(kind: CraftWaterKind, heightM: number, hullM: number): 'wake' | 'downwash' | null {
  if (!(heightM > -3)) return null; // drawn under the surface (a bed-seated hull): nothing to disturb from above
  if (kind === 'hover') return heightM <= HOVER_ON_WATER_M ? 'wake' : null;
  return heightM < downwashBand(hullM) ? 'downwash' : null;
}
/** A wake stamped at a lower rate than the fixed tick carries the ticks it stands for (≤ 3×: a hitch is not a bomb). */
export function wakeRateScale(dtS: number): number { return clamp(dtS * 60, 1, 3); }
/** Spray a moving hover craft throws off its skirt over water (NPC craft; a player's own VehicleFx throws its own). */
export function hoverSprayPerS(speedMps: number): number { return speedMps > 6 ? Math.min(40, speedMps * 0.9) : 0; }

// ── W5: voices ─────────────────────────────────────────────────────────────────────────────────────────────────────
/** The cushion's hiss on water (the spray loop): an idle hiss at rest, a rising rush with speed. 0 off the water. */
export function hoverSprayMix(onWater: boolean, speedMps: number, out?: { gain: number; rate: number }): { gain: number; rate: number } {
  const o = out ?? { gain: 0, rate: 1 };
  if (!onWater) { o.gain = 0; o.rate = 1; return o; }
  const v = clamp01(speedMps / 40);
  o.gain = 0.07 + 0.38 * v; o.rate = 0.8 + 0.45 * v;
  return o;
}
/** Strokes a second at a crawl of `speedMps` (arm entries: ~0.8 drifting → 1.4 at the full crawl). */
export function strokeCadenceHz(speedMps: number): number { return 0.8 + 0.6 * clamp01(speedMps / SPEED_SWIM_MPS); }
/** A swimmer's stroke clock: time-driven while moving (a stroke is not a stride), the first stroke soon after starting. */
export class StrokeClock {
  private acc = 0.5;
  advance(dtS: number, speedMps: number): number {
    if (!(speedMps > 0.3) || !(dtS > 0) || dtS > 0.5) { this.acc = 0.5; return 0; }
    this.acc += dtS * strokeCadenceHz(speedMps);
    let n = 0;
    while (this.acc >= 1 && n < 2) { this.acc -= 1; n++; }
    if (this.acc > 1) this.acc = 0.5;
    return n;
  }
  reset(): void { this.acc = 0.5; }
}

// ── W6: wetness ────────────────────────────────────────────────────────────────────────────────────────────────────
/** Water over the feet from which the body is soaked past the hips (a deep wade counts as having been IN it). */
export const DEEP_WADE_M = 0.6;
/** A fully soaked swimmer drips this long after climbing out. */
export const DRIP_S = 6;
/** …and leaves wet prints this long. */
export const WET_PRINTS_S = 20;
/** A wet print's own life (it dries; the fade tail is the footprint module's). */
export const WET_PRINT_LIFE_S = 18;

export interface Wetness {
  inWater: boolean;
  /** How soaked, 0..1 (a swim = 1, a deep wade 0.45…0.85 by depth). */
  soak: number;
  dripLeft: number; dripTotal: number;
  printLeft: number; printTotal: number;
  exits: number;
}
export function newWetness(): Wetness { return { inWater: false, soak: 0, dripLeft: 0, dripTotal: 0, printLeft: 0, printTotal: 0, exits: 0 }; }

/**
 * One step of a body's wetness. `depthM` = water over the ground under the feet (0 dry), `swimming` = afloat or
 * under. In deep water the soak builds and nothing drips (the water is all around); the step that LEAVES deep water
 * starts the drip and the wet-print clocks, scaled by the soak, and returns true (the climb-out). Afterwards both run
 * down. Re-entering stops them.
 */
export function wetnessStep(w: Wetness, depthM: number, swimming: boolean, dt: number): boolean {
  const deep = swimming || depthM >= DEEP_WADE_M;
  if (deep) {
    const soak = swimming ? 1 : 0.45 + 0.4 * clamp01((depthM - DEEP_WADE_M) / (SWIM_DEPTH_M - DEEP_WADE_M));
    w.soak = Math.max(w.soak, soak);
    w.inWater = true;
    w.dripLeft = 0; w.printLeft = 0;
    return false;
  }
  if (w.inWater) {
    w.inWater = false;
    w.dripTotal = w.dripLeft = DRIP_S * (0.4 + 0.6 * w.soak);
    w.printTotal = w.printLeft = WET_PRINTS_S * (0.5 + 0.5 * w.soak);
    w.soak = 0;
    w.exits++;
    return true;
  }
  const d = Math.max(0, dt);
  w.dripLeft = Math.max(0, w.dripLeft - d);
  w.printLeft = Math.max(0, w.printLeft - d);
  return false;
}
/** Drips a second now: the tier's budget at the climb-out, tapering ((left / total)^1.5) to nothing. */
export function dripRate(w: Wetness, budgetPerS: number): number {
  if (!(w.dripLeft > 0) || !(w.dripTotal > 0) || !(budgetPerS > 0)) return 0;
  return budgetPerS * clamp01(w.dripLeft / w.dripTotal) ** 1.5;
}
/** How wet a print laid now is, 0..1 (0 = a dry scuff). */
export function printWetness(w: Wetness): number {
  return w.printLeft > 0 && w.printTotal > 0 ? clamp01(w.printLeft / w.printTotal) : 0;
}
/**
 * A wet print laid late in the window dries sooner: its birth is moved back by up to (life − tail) × (1 − wet), so a
 * fresh climb-out's print lives its whole life and the last damp one only its fade. Returns seconds (≥ 0).
 */
export function wetPrintBornShift(wet01: number, lifeS: number, tailS: number): number {
  return Math.max(0, (1 - clamp01(wet01)) * Math.max(0, lifeS - tailS) * 0.85);
}

// ── W7: the tier ruling ────────────────────────────────────────────────────────────────────────────────────────────
/** Drips a second at the climb-out, by tier (before the particle-density scale). */
export const DRIP_BUDGET_PER_S: Readonly<Record<string, number>> = { minimal: 0, potato: 14, low: 18, med: 26, high: 34, ultra: 40 };

export interface WaterFxTier {
  /** Ripple-field stamps (rings, wakes, downwash dishes): the "Realistic fluid physics" flag. */
  rings: boolean;
  /** Splash / spray / drip particles: the particle budget, never on minimal. */
  particles: boolean;
  /** Scale on spray and drip rates (0.4 + 0.6 × the particle density). */
  particleScale: number;
  dripsPerS: number;
  /** Plops, hisses, strokes, the spray and downwash loops: every tier (audio is not the GPU's cost). */
  sounds: boolean;
}

/**
 * THE W7 RULING. minimal: nothing on the GPU — no particles at any density; rings only if the player turns the fluid
 * flag on themselves (its default is off, and the field is a CPU wave equation + one small texture). potato: rings off
 * by its default flag; splash / spray / drip particles exactly when its particle density is above 0 (its default is
 * 0 → none). low and up: rings on (their flag), particles by their density. Sounds everywhere.
 */
export function waterFxTier(tier: string, fluidPhysics: boolean, particleCap: number): WaterFxTier {
  const particles = tier !== 'minimal' && particleCap > 0;
  const particleScale = particles ? 0.4 + 0.6 * clamp01(particleCap) : 0;
  return { rings: fluidPhysics, particles, particleScale, dripsPerS: particles ? (DRIP_BUDGET_PER_S[tier] ?? DRIP_BUDGET_PER_S.med) * particleScale : 0, sounds: true };
}
